import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import '../ImportCustomerFinancials/NewConnectionModal.scss';
import './DestinationStep.scss';

import { Button } from '../design-system/components';

import { PortalTextCommon } from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import { assertNever } from '../../Utils/AssertNever';
import {
    FieldGuideCompanySelection,
    FieldGuideEngagementSelection,
    FieldGuideUploadState,
    GetFieldGuideDisplayName,
    GetFieldGuideEmail,
    GetFieldGuideSelectedCompany,
    GetFieldGuideSelectedEngagement,
    GetFieldGuideUploadState,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';
import { GetPortalText } from '../../Store/PortalAssets';

import { CompanyStepContainer } from './CompanyStepContainer';
import { DestinationAccount, DestinationFrame } from './DestinationFrame';
import { DestinationStep } from './DestinationStepper';
import { EngagementStepContainer } from './EngagementStepContainer';

type InjectedReduxState = {
    uploadState: FieldGuideUploadState | undefined;
    selectedCompany: FieldGuideCompanySelection | undefined;
    selectedEngagement: FieldGuideEngagementSelection | undefined;
    commonText: PortalTextCommon | undefined;
    fieldGuideDisplayName: string | undefined;
    fieldGuideEmail: string | undefined;
};

type InjectedActionCreators = {
    BeginUpload: () => void;
    PreviousUploadStep: () => void;
};

type DestinationStepProps = {
    onCancel: () => void;
};

type Props = DestinationStepProps & InjectedReduxState & InjectedActionCreators;

function activeStepFor(uploadState: FieldGuideUploadState | undefined): DestinationStep {
    switch (uploadState) {
        case undefined:
        case FieldGuideUploadState.SelectingCompany:
            return 'company';
        case FieldGuideUploadState.SelectingEngagement:
            return 'engagement';
        case FieldGuideUploadState.MappingRequests:
            return 'request';
        case FieldGuideUploadState.Reviewing:
        case FieldGuideUploadState.Sending:
        case FieldGuideUploadState.Succeeded:
        case FieldGuideUploadState.Failed:
            return 'push';
        default:
            return assertNever(uploadState);
    }
}

/** The rail's account: the display name with the bare email as its detail, or the email alone (no detail) when that is all that is known. */
function accountFor(displayName: string | undefined, email: string | undefined): DestinationAccount | undefined {
    if (displayName) {
        return { name: displayName, detail: email };
    }

    return email ? { name: email } : undefined;
}

export const DestinationStepComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        onCancel,
        uploadState,
        selectedCompany,
        selectedEngagement,
        commonText,
        fieldGuideDisplayName,
        fieldGuideEmail,
        BeginUpload,
        PreviousUploadStep,
    } = props;

    React.useEffect(() => {
        if (uploadState === undefined) {
            BeginUpload();
        }
    // Mount-only: begins the upload once per step entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeStep = activeStepFor(uploadState);

    const headings: { [step in DestinationStep]: string } = {
        company: commonText?.fieldGuideSelectClientHeading || 'Select a client',
        engagement: commonText?.fieldGuideSelectEngagementHeading || 'Select an engagement',
        request: commonText?.fieldGuideSelectRequestHeading || 'Select a request',
        // The review and send steps are not built yet; until then nothing renders there.
        push: '',
    };

    // Stand-in for the request step, which is not built yet: Cancel and Back only, so the rail and Back can be exercised.
    const renderPlaceholder = (): React.ReactNode => (
        <div className={'field-guide-step'}>
            <div className={'field-guide-step__content page-stack'} />
            <div className={'field-guide-footer'}>
                <Button type={'floating'} onClick={onCancel}>{commonText?.cancel || 'Cancel'}</Button>
                <div className={'cr-modal-footer__button-group'}>
                    <Button type={'secondary'} className={'page-btn--min-width'} onClick={PreviousUploadStep}>{commonText?.backButton || 'Back'}</Button>
                </div>
            </div>
        </div>
    );

    const renderBody = (): React.ReactNode => {
        switch (uploadState) {
            case FieldGuideUploadState.SelectingCompany:
                return <CompanyStepContainer onCancel={onCancel} />;
            case FieldGuideUploadState.SelectingEngagement:
                return <EngagementStepContainer onCancel={onCancel} />;
            case FieldGuideUploadState.MappingRequests:
                return renderPlaceholder();
            // BeginUpload has not landed yet, or the review and send steps are not built.
            case undefined:
            case FieldGuideUploadState.Reviewing:
            case FieldGuideUploadState.Sending:
            case FieldGuideUploadState.Succeeded:
            case FieldGuideUploadState.Failed:
                return null;
            default:
                return assertNever(uploadState);
        }
    };

    return (
        <DestinationFrame
            heading={headings[activeStep]}
            onClose={onCancel}
            activeStep={activeStep}
            stepLabels={{
                client: commonText?.fieldGuideStepClient || 'Client',
                engagement: commonText?.fieldGuideStepEngagement || 'Engagement',
                request: commonText?.fieldGuideStepRequest || 'Request',
                send: commonText?.fieldGuideStepSend || 'Send to Fieldguide',
            }}
            logoAlt={commonText?.fieldGuideLogoAlt || 'Send to Fieldguide'}
            companyName={selectedCompany ? selectedCompany.name : undefined}
            engagementName={selectedEngagement ? selectedEngagement.name : undefined}
            account={accountFor(fieldGuideDisplayName, fieldGuideEmail)}
        >
            {renderBody()}
        </DestinationFrame>
    );
};

export const DestinationStepContainer = connect<InjectedReduxState, InjectedActionCreators, DestinationStepProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            uploadState: GetFieldGuideUploadState(appState),
            selectedCompany: GetFieldGuideSelectedCompany(appState),
            selectedEngagement: GetFieldGuideSelectedEngagement(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            fieldGuideDisplayName: GetFieldGuideDisplayName(appState),
            fieldGuideEmail: GetFieldGuideEmail(appState),
        };
        return result;
    },
    {
        BeginUpload: FieldGuideStoreActions.BeginUpload,
        PreviousUploadStep: FieldGuideStoreActions.PreviousUploadStep,
    }
)(DestinationStepComponent);
